import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  formatMoney as formatMoneyLib,
  formatMoneyString as formatMoneyStringLib,
  getCreditCurrencyEqlCode,
} from "@scrile/tools/dist/lib/MoneyHelpers";
import { getCurrencySymbol } from "@scrile/tools/dist/lib/MoneyHelpers";
import store from "@/store";
import { $t } from "@/lib/i18nHelpers";
import { getLocale } from "@/lib/localeHelper";
import config from "@/config";

export { getCurrencySymbol };

export function getCurrencyCode() {
  return getCreditCurrencyEqlCode();
}

export const currencySymbol = $t(getCurrencySymbol(getCurrencyCode()));

export function getMainCurrencyCode() {
  return store.state.systemSettings?.mainCurrency ?? "USD";
}

interface FormatMoneyOpts {
  currencyCode?: string;
  forceNumberSymbol?: boolean;
}

export interface FullPriceData {
  creditAmount: number;
  cost: number;
  creditPrice: string;
  price: string;
}

export function formatMoney(
  amount: number,
  { currencyCode = getCurrencyCode(), forceNumberSymbol = false }: FormatMoneyOpts = {}
) {
  return formatMoneyLib(amount, {
    currencyCode: currencyCode,
    locale: getLocale(),
    creditsSymbolTranslator: $t,
    useGrouping: config.moneyUseGrouping,
    forceNumberSymbol,
  });
}

export function formatMoneyString(
  amount: number,
  { currencyCode = getCurrencyCode(), forceNumberSymbol = false }: FormatMoneyOpts = {}
) {
  return formatMoneyStringLib(amount, {
    currencyCode,
    locale: getLocale(),
    creditsSymbolTranslator: $t,
    useGrouping: config.moneyUseGrouping,
    forceNumberSymbol,
  });
}

export function getShrinkedNumber(value: number): string {
  if (value > 999999) return `${Math.floor(value / 1000000)}M`;
  if (value > 999) return `${Math.floor(value / 1000)}K`;
  return `${value}`;
}

export function printPaymentPackageName(name: string) {
  return name ? `${name}, ` : "";
}

export function getMainCurrencyPackageCost(paymentPackage: PaymentPackage) {
  return paymentPackage.costs.find(i => i.currency === getMainCurrencyCode())?.cost;
}

export function getMainCurrencyPackageCostString(paymentPackage: PaymentPackage) {
  const cost = getMainCurrencyPackageCost(paymentPackage);
  return !cost ? undefined : formatMoneyString(cost, { currencyCode: getMainCurrencyCode() });
}

export function printPaymentPackage(paymentPackage: PaymentPackage) {
  return `Cr: ${formatMoneyString(paymentPackage.amount)}, fiat: ${getMainCurrencyPackageCostString(paymentPackage)}`;
}

export function getCost(pricePackage: PaymentPackage): number {
  return pricePackage.amount;
}

export function getCostByCurrency(p: PaymentPackage | null | undefined, currency: string = getMainCurrencyCode()) {
  if (!p) return;
  return p.costs.find(i => i.currency === currency);
}

export function getCostByCurrencyFromPackages(
  packages: PaymentPackage[],
  comparator: (i: PaymentPackage) => boolean,
  currency: string = getMainCurrencyCode()
) {
  const paymentPackage = packages.find(comparator);
  if (!paymentPackage) return 0;
  const cost = getCostByCurrency(paymentPackage, currency);
  if (!cost) return 0;
  return cost.cost;
}

export function getFullPriceData(paymentPackage?: PaymentPackage | null): FullPriceData {
  const creditAmount = paymentPackage ? getCost(paymentPackage) : 0;
  const cost = getCostByCurrency(paymentPackage)?.cost ?? 0;
  return {
    creditAmount,
    cost,
    creditPrice: creditAmount ? formatMoneyString(creditAmount) : "",
    price: cost ? formatMoneyString(cost, { currencyCode: getMainCurrencyCode() }) : "",
  };
}
